import React, { useEffect, useState } from 'react';
import { Note, Question } from '../types';
// Direct import of the specific services
import { NoteService } from '../services/note.service';
import { QuestionService } from '../services/question.service';

interface NotesQuestionsPanelProps {
  chapterId: number | null;
}

type ChangedItem = {
  type: 'note' | 'question';
  id: number;
  content?: string;
  question?: string;
  answer?: string;
};

const NotesQuestionsPanel: React.FC<NotesQuestionsPanelProps> = ({ chapterId }) => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [questionsMap, setQuestionsMap] = useState<Record<number, Question[]>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddNoteForm, setShowAddNoteForm] = useState<boolean>(false);
  const [newNoteContent, setNewNoteContent] = useState<string>('');
  const [changedItems, setChangedItems] = useState<ChangedItem[]>([]);

  useEffect(() => {
    const fetchNotesAndQuestions = async () => {
      if (!chapterId) return;
      
      try {
        setLoading(true);
        // Fetch notes for the chapter using NoteService
        const notesData = await NoteService.getNotesByChapterId(chapterId);
        setNotes(notesData);
        
        // Fetch questions for each note using QuestionService
        const questionsData: Record<number, Question[]> = {};
        for (const note of notesData) {
          const noteQuestions = await QuestionService.getQuestionsByNoteId(note.id);
          questionsData[note.id] = noteQuestions;
        }
        setQuestionsMap(questionsData);
        
        setError(null);
      } catch (err) {
        console.error('Error fetching notes and questions:', err);
        setError('Failed to fetch notes and questions. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchNotesAndQuestions();
    // Reset state when changing chapters
    setShowAddNoteForm(false);
    setNewNoteContent('');
    setChangedItems([]);
  }, [chapterId]);

  const handleNoteContentChange = (note: Note, newContent: string) => {
    // Update local state
    const updatedNotes = notes.map(n => 
      n.id === note.id ? {...n, content: newContent} : n
    );
    setNotes(updatedNotes);
    
    // Track the change
    const existingChangeIndex = changedItems.findIndex(
      item => item.type === 'note' && item.id === note.id
    );
    
    if (existingChangeIndex >= 0) {
      const updatedChanges = [...changedItems];
      updatedChanges[existingChangeIndex].content = newContent;
      setChangedItems(updatedChanges);
    } else {
      setChangedItems([...changedItems, {
        type: 'note',
        id: note.id,
        content: newContent
      }]);
    }
  };

  const handleQuestionChange = (question: Question, field: 'question' | 'answer', newValue: string) => {
    // Update local state
    const noteQuestions = questionsMap[question.note_id];
    const updatedQuestions = noteQuestions.map(q => 
      q.id === question.id ? {...q, [field]: newValue} : q
    );
    
    setQuestionsMap({
      ...questionsMap,
      [question.note_id]: updatedQuestions
    });
    
    // Track the change
    const existingChangeIndex = changedItems.findIndex(
      item => item.type === 'question' && item.id === question.id
    );
    
    if (existingChangeIndex >= 0) {
      const updatedChanges = [...changedItems];
      updatedChanges[existingChangeIndex] = {
        ...updatedChanges[existingChangeIndex],
        [field]: newValue
      };
      setChangedItems(updatedChanges);
    } else {
      setChangedItems([...changedItems, {
        type: 'question',
        id: question.id,
        [field]: newValue
      }]);
    }
  };

  const addNewQuestion = (noteId: number) => {
    // In a real implementation, this would call QuestionService to create a new question
    // For now, we'll create a temporary question with a negative ID
    const tempId = -Date.now(); // Using negative timestamp as temporary ID
    const newQuestion: Question = {
      id: tempId,
      question: '',
      answer: '',
      note_id: noteId,
      is_active: true
    };
    
    setQuestionsMap({
      ...questionsMap,
      [noteId]: [...(questionsMap[noteId] || []), newQuestion]
    });
  };

  const deleteQuestion = async (question: Question) => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      try {
        // For questions with positive IDs (existing in the database), call the API
        if (question.id > 0) {
          await QuestionService.deleteQuestion(question.id);
        }
        
        // Update local state
        const noteQuestions = questionsMap[question.note_id];
        const updatedQuestions = noteQuestions.filter(q => q.id !== question.id);
        
        setQuestionsMap({
          ...questionsMap,
          [question.note_id]: updatedQuestions
        });
        
        // Show success message
        alert('Question deleted successfully');
      } catch (err) {
        console.error('Error deleting question:', err);
        alert('Failed to delete question. Please try again later.');
      }
    }
  };

  const handleAddNote = () => {
    setShowAddNoteForm(true);
  };

  const handleSaveNewNote = async () => {
    if (!newNoteContent.trim()) {
      alert('Please enter note content.');
      return;
    }
    
    if (!chapterId) {
      alert('No chapter selected');
      return;
    }
    
    try {
      // Create new note using NoteService
      const newNote = await NoteService.createNote({
        content: newNoteContent.trim(),
        chapter_id: chapterId
      });
      
      // Update local state
      setNotes([...notes, newNote]);
      
      // Initialize empty questions array for this note
      setQuestionsMap({
        ...questionsMap,
        [newNote.id]: []
      });
      
      // Clear form and hide it
      setShowAddNoteForm(false);
      setNewNoteContent('');
      
      // Show success message
      alert(`New note was successfully created!`);
    } catch (err) {
      console.error('Error creating note:', err);
      alert('Failed to create note. Please try again later.');
    }
  };

  const handleCancelAddNote = () => {
    setShowAddNoteForm(false);
    setNewNoteContent('');
  };

  const saveAllChanges = async () => {
    if (changedItems.length === 0) {
      alert('No changes to save.');
      return;
    }
    
    try {
      for (const item of changedItems) {
        if (item.type === 'note' && item.content) {
          // Update note using NoteService
          await NoteService.updateNote(item.id, { content: item.content });
        } else if (item.type === 'question') {
          // Update question using QuestionService
          const updateData: { question?: string; answer?: string } = {};
          if (item.question !== undefined) updateData.question = item.question;
          if (item.answer !== undefined) updateData.answer = item.answer;
          
          await QuestionService.updateQuestion(item.id, updateData);
        }
      }
      
      // Clear changes after successful save
      setChangedItems([]);
      
      // Show success message
      alert(`Successfully saved ${changedItems.length} changes!`);
    } catch (err) {
      console.error('Error saving changes:', err);
      alert('Failed to save some changes. Please try again later.');
    }
  };

  if (!chapterId) {
    return null;
  }

  if (loading) {
    return <div className="section">Loading notes and questions...</div>;
  }

  return (
    <div className="section">
      <h2>Notes & Questions</h2>
      {error && <div className="error">{error}</div>}
      
      <div id="notes-questions-list">
        {notes.map((note, index) => (
          <div key={note.id} className="note-item" data-id={note.id}>
            <div className="note-number">Note #{index + 1}</div>
            <textarea
              className="note-content"
              value={note.content}
              onChange={(e) => handleNoteContentChange(note, e.target.value)}
              data-id={note.id}
            />
            
            <div className="question-list">
              {questionsMap[note.id]?.map((question) => (
                <div key={question.id} className="question-item">
                  <div className="question-fields">
                    <div className="question-field">
                      <label>Question:</label>
                      <textarea
                        className="question-text"
                        value={question.question}
                        onChange={(e) => handleQuestionChange(question, 'question', e.target.value)}
                        data-id={question.id}
                      />
                    </div>
                    <div className="question-field">
                      <label>Answer:</label>
                      <textarea
                        className="answer-text"
                        value={question.answer}
                        onChange={(e) => handleQuestionChange(question, 'answer', e.target.value)}
                        data-id={question.id}
                      />
                    </div>
                  </div>
                  <button 
                    className="delete-btn"
                    onClick={() => deleteQuestion(question)}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
            
            <button 
              className="add-question-btn"
              onClick={() => addNewQuestion(note.id)}
            >
              Add Question
            </button>
          </div>
        ))}
      </div>
      
      {!showAddNoteForm ? (
        <button 
          id="add-note-btn" 
          className="add-btn"
          onClick={handleAddNote}
        >
          Add Note
        </button>
      ) : (
        <div id="add-note-form">
          <textarea 
            id="new-note-content" 
            placeholder="Note Content"
            value={newNoteContent}
            onChange={(e) => setNewNoteContent(e.target.value)}
          />
          <button id="save-note-btn" onClick={handleSaveNewNote}>Save</button>
          <button id="cancel-note-btn" onClick={handleCancelAddNote}>Cancel</button>
        </div>
      )}
      
      <div className="save-container">
        <button 
          id="save-all-btn" 
          className="save-all"
          onClick={saveAllChanges}
          disabled={changedItems.length === 0}
        >
          Save All Changes ({changedItems.length})
        </button>
      </div>
    </div>
  );
};

export default NotesQuestionsPanel;